import { onUnmounted, ref, watch } from 'vue';

import { wrapperRaf } from '@components/utils';

import type { GetKey } from '../interface';
import type { Ref, ShallowRef, VNodeProps } from 'vue';

export type CacheMap = Map<any, number>;

export function useHeights<T>(
  mergedData: ShallowRef<any[]>,
  getKey: GetKey<T>,
  onItemAdd?: ((item: T) => void) | null,
  onItemRemove?: ((item: T) => void) | null
): {
  setInstance: (item: T, instance: HTMLElement) => void;
  collectHeight: () => void;
  heights: CacheMap;
  updatedMark: Ref<symbol>;
} {
  const instance = new Map<VNodeProps['key'], HTMLElement>();
  const heights = new Map();
  const updatedMark = ref(Symbol('update'));

  watch(mergedData, () => {
    updatedMark.value = Symbol('update');
  });

  let collectRaf: number | void = undefined;

  function cancelRaf() {
    wrapperRaf.cancel(collectRaf!);
  }

  function collectHeight() {
    cancelRaf();

    collectRaf = wrapperRaf(() => {
      instance.forEach((element, key) => {
        if (element && element.offsetParent) {
          const { offsetHeight } = element;

          if (heights.get(key) !== offsetHeight) {
            updatedMark.value = Symbol('update');
            heights.set(key, element.offsetHeight);
          }
        }
      });
    });
  }

  function setInstance(item: T, element: HTMLElement) {
    const key = getKey(item);
    const origin = instance.get(key);

    if (element) {
      instance.set(key, (element as any).$el || element);
      collectHeight();
    } else {
      instance.delete(key);
    }

    if (!origin !== !element) {
      if (element) {
        onItemAdd?.(item);
      } else {
        onItemRemove?.(item);
      }
    }
  }

  onUnmounted(() => {
    cancelRaf();
  });

  return { setInstance, collectHeight, heights, updatedMark };
}
